/**
 * @fileoverview Configurações → Faturamento (31/10/2026, etapa 7 dos planos).
 * Para a Empresa nos planos novos: o plano numa frase, com a situação e a
 * mensalidade; a renovação e a forma de pagamento; o pedido aguardando
 * pagamento (o boleto, com a linha digitável e o link; dá para desistir); o
 * uso do mês contra os limites; as faturas; e, embaixo, os dados para a
 * cobrança. No Starter, contratar o Basic ou o Pro, ou só o Chat Interno, abre
 * o modal da contratação. As Empresas de antes dos planos (o servidor responde
 * `legacy`) seguem em cortesia até o super migrá-las: veem só um aviso com o
 * contato da Sttelar (o painel de pedidos antigo saiu na etapa 12). Relê
 * quando o servidor avisa pelo socket que a cobrança mudou (o pagamento
 * confirmou, o pedido expirou).
 * Etapa 9: cancelar o plano (vale até o fim do ciclo pago; desfazer contrata de
 * novo a partir do dia seguinte), a escolha do que fica num plano menor, o
 * acesso restrito (só esta tela até a escolha) e os itens arquivados, com
 * "Restaurar" quando o plano deixa. Etapa 10: o atraso no pagamento, no topo
 * (até quando pagar, ou a suspensão, e como pagar: Pix, cartão ou o boleto).
 */

import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Paper } from "@material-ui/core";
import { useHistory, useLocation } from "react-router-dom";
import { Copy, ExternalLink, RefreshCw } from "lucide-react";
import { toast } from "react-toastify";

import BillingProfileForm from "../BillingProfileForm";
import AdequacyDialog from "../Billing/AdequacyDialog";
import PlansDialog from "../Billing/PlansDialog";
import { billingErrorText } from "../Billing/PlanControls";
import PixPayment from "../Billing/PixPayment";
import ReductionDialog from "../Billing/ReductionDialog";
import RefundDialog from "../Billing/RefundDialog";
import { LoadError, LoadingState } from "../Settings/LoadState";
import { InfoTip } from "../../pages/Flows/components/TriageManager/InfoTip";
import { SocketContext } from "../../context/Socket/SocketContext";
import { useBilling } from "../../context/Billing/BillingContext";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import { capitalize, describeTerms, money, sentence, thousands } from "../../utils/billingPlans";
import { boletoDigits, formatBoletoLine } from "../../utils/boletoLine";
import { formatDate, formatSaoPauloDate, formatUtcDate } from "../../utils/formatLocaleDate";
import { sttelarWhatsAppUrl } from "../../utils/sttelarContact";
import useStyles from "./styles";

const t = (key, options) => i18n.t(`billingOverview.${key}`, options);

const STATUS_TONE = { paid: "ok", overpaid: "ok", pending: "wait", processing: "wait", underpaid: "wait", failed: "bad", canceled: "bad", refunded: "bad", chargedback: "bad" };

/**
 * Uma linha do uso. Só as mensagens (`consumable`) ganham cor: elas acabam no
 * mês e bloqueiam. Pessoas, contas, números e quadros no limite são o normal
 * do plano (o Starter tem uma conta e usa uma).
 */
const UsageRow = ({ classes, name, used, limit, consumable = false }) => {
  const ratio = limit ? Math.min(used / limit, 1) : 0;
  const tone = !consumable || !limit ? "" : used >= limit ? classes.meterFull : used / limit >= 0.8 ? classes.meterWarn : "";
  const value =
    limit === null
      ? t("usage.unlimited", { used: thousands(used) })
      : limit === 0 && used === 0
        ? t("usage.notContracted")
        : t("usage.of", { used: thousands(used), limit: thousands(limit) });
  return (
    <li className={classes.usageRow}>
      <span className={classes.usageName}>{name}</span>
      <div className={classes.meter} aria-hidden="true">
        {limit ? <div className={`${classes.meterFill} ${tone}`} style={{ width: `${Math.max(ratio * 100, used > 0 ? 2 : 0)}%` }} /> : <div className={classes.meterNone} />}
      </div>
      <span className={classes.usageValue}>{value}</span>
    </li>
  );
};

/** Os itens arquivados (etapa 9): guardados 12 meses; "Restaurar" volta o item se o plano deixar. */
const ArchivedCard = ({ classes, count, onRestored }) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/billing/archived");
      setItems(data.archived);
    } catch (err) {
      toastError(err);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, count, load]);

  const restore = async (item) => {
    setBusy(item.id);
    try {
      await api.post(`/billing/archived/${item.id}/restore`);
      toast.success(t("archived.restored", { name: item.label }));
      onRestored();
    } catch (err) {
      toast.error(billingErrorText(err?.response?.data?.error));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Paper elevation={0} className={classes.card} component="section" aria-labelledby="billing-archived-title">
      <div className={classes.cardHead}>
        <h3 className={classes.cardTitle} id="billing-archived-title">
          {t("archived.title", { count })}
          <InfoTip text={t("archived.info")} />
        </h3>
        <button type="button" className={classes.secondaryBtn} onClick={() => setOpen((value) => !value)} aria-expanded={open} aria-controls="billing-archived-list">
          {open ? t("archived.hide") : t("archived.show")}
        </button>
      </div>
      {open && (
        <div id="billing-archived-list">
          {!items ? (
            <p className={classes.empty}>{t("archived.loading")}</p>
          ) : items.length === 0 ? (
            <p className={classes.empty}>{t("archived.empty")}</p>
          ) : (
            <ul className={classes.archivedList}>
              {items.map((item) => (
                <li key={item.id} className={classes.archivedRow}>
                  <div className={classes.archivedMain}>
                    <span className={classes.archivedName}>
                      {item.label || t(`archived.types.${item.resourceType}`)}
                      <span className={classes.archivedType}>{t(`archived.types.${item.resourceType}`)}</span>
                    </span>
                    <span className={classes.archivedMeta}>
                      {item.account ? `${t("archived.account", { name: item.account.name })}${item.account.archived ? ` (${t("archived.accountArchived")})` : ""} · ` : ""}
                      {t("archived.dates", { archived: formatDate(item.archivedAt), until: formatDate(item.retainedUntil) })}
                    </span>
                  </div>
                  <button type="button" className={classes.secondaryBtn} onClick={() => restore(item)} disabled={busy !== null}>
                    {busy === item.id ? t("archived.restoring") : t("archived.restore")}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Paper>
  );
};

const copyBoletoLine = async (line) => {
  try {
    await navigator.clipboard.writeText(boletoDigits(line));
    toast.success(t("pending.copied"));
  } catch (err) {
    toast.info(boletoDigits(line));
  }
};

/**
 * O atraso no pagamento (etapa 10): a fatura vencida, até quando pagar (ou a
 * suspensão) e como pagar: o Pix no lugar dela (um só, mostrado de novo
 * enquanto vale), de novo no cartão salvo, ou o boleto que ainda pode estar em
 * confirmação. Pago, o servidor avisa e a tela relê.
 */
const OverdueCard = ({ classes, delinquency, subscription, onReload }) => {
  const [busy, setBusy] = useState(null);
  const [createdPix, setCreatedPix] = useState(null);
  const [expired, setExpired] = useState(false);
  const pix = createdPix || delinquency.pix;
  const expiresAt = pix?.expiresAt || null;
  // Vencido o Pix, ele mesmo diz que venceu e "Pagar no Pix" volta (um novo entra no lugar).
  useEffect(() => {
    setExpired(false);
    if (!expiresAt) return undefined;
    const wait = new Date(expiresAt).getTime() - Date.now();
    if (wait <= 0) {
      setExpired(true);
      return undefined;
    }
    const timer = setTimeout(() => setExpired(true), wait);
    return () => clearTimeout(timer);
  }, [expiresAt]);
  const onlyInternal = delinquency.scope === "internal_chat";
  const what = onlyInternal ? t("overdue.whatInternal") : t("overdue.whatSending");
  const amount = money(delinquency.amountCents);
  const due = delinquency.since ? formatUtcDate(delinquency.since) : "";
  // O "pague até" já conta o aviso de 2 dias e o prazo do suporte; sem ele, nada vai parar.
  const deadline = delinquency.payBy ? formatUtcDate(delinquency.payBy) : "";
  const settlingBoletos = delinquency.charges.filter((charge) => charge.settling && charge.boletoLine);
  const card =
    subscription?.cardBrand && subscription?.cardLastFour
      ? t("overdue.retryCard", { brand: capitalize(subscription.cardBrand), last4: subscription.cardLastFour })
      : t("overdue.retrySavedCard");

  const failed = (err) => {
    const code = err?.response?.data?.error;
    if (code) toast.error(billingErrorText(code));
    else toastError(err);
    onReload();
  };

  const payWithPix = async () => {
    setBusy("pix");
    try {
      const { data } = await api.post("/billing/overdue/pix");
      setCreatedPix(data.pix);
    } catch (err) {
      failed(err);
    } finally {
      setBusy(null);
    }
  };

  const retryCard = async () => {
    setBusy("retry");
    try {
      await api.post("/billing/overdue/retry");
      toast.success(t("overdue.retryPaid"));
      onReload();
    } catch (err) {
      failed(err);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Paper elevation={0} className={`${classes.card} ${delinquency.suspended ? classes.suspendedCard : classes.restrictedCard}`} component="section" aria-labelledby="billing-overdue-title" role="region">
      <div className={classes.cardHead}>
        <h2 className={classes.cardTitle} id="billing-overdue-title">
          {delinquency.suspended ? (onlyInternal ? t("overdue.titleSuspendedInternal") : t("overdue.titleSuspended")) : t("overdue.title")}
        </h2>
        {!delinquency.suspended && deadline && <span className={classes.cardMeta}>{t("overdue.deadline", { date: deadline })}</span>}
      </div>
      <p className={classes.pendingText}>
        {delinquency.suspended
          ? t(onlyInternal ? "overdue.textSuspendedInternal" : "overdue.textSuspended", { amount, due })
          : deadline
            ? t("overdue.text", { amount, due, date: deadline, what })
            : t("overdue.textNoDeadline", { amount, due })}
      </p>
      {delinquency.charges.some((charge) => charge.dispute) && <p className={classes.pendingText}>{t("overdue.disputeNote")}</p>}
      {delinquency.charges.length > 1 && (
        <ul className={classes.overdueList}>
          {delinquency.charges.map((charge) => (
            <li key={charge.id} className={classes.overdueItem}>
              <span>{charge.dispute ? t("overdue.invoiceDispute", { date: formatUtcDate(charge.dueAt) }) : t("overdue.invoice", { date: formatUtcDate(charge.dueAt) })}</span>
              <span className={classes.overdueAmount}>{money(charge.amountCents)}</span>
            </li>
          ))}
        </ul>
      )}
      {pix && (
        <div className={classes.pixBlock}>
          <PixPayment pix={pix} amountCents={pix.amountCents} expiredHint={t("overdue.pixExpiredHint")} />
        </div>
      )}
      {settlingBoletos.map((charge) => (
        <div key={charge.id} className={classes.boletoLine}>
          <span className={classes.boletoDigits}>
            <span className={classes.boletoLabel}>{t("pending.line")}</span>
            {formatBoletoLine(charge.boletoLine)}
          </span>
          <button type="button" className={classes.secondaryBtn} onClick={() => copyBoletoLine(charge.boletoLine)}>
            <Copy />
            {t("pending.copy")}
          </button>
          {charge.boletoUrl && (
            <a className={classes.secondaryBtn} href={charge.boletoUrl} target="_blank" rel="noopener noreferrer">
              <ExternalLink />
              {t("pending.open")}
            </a>
          )}
        </div>
      ))}
      {settlingBoletos.length > 0 && <p className={classes.cancelHint}>{t("overdue.boletoSettling")}</p>}
      <div className={classes.pendingActions}>
        {(!pix || expired) && delinquency.canPayWithPix && (
          <button type="button" className={classes.primaryBtn} onClick={payWithPix} disabled={!!busy}>
            {busy === "pix" ? t("overdue.creatingPix") : t("overdue.payPix")}
          </button>
        )}
        {/* Com o Pix aberto, o cartão espera (senão, paga duas vezes). */}
        {delinquency.canRetryCard && (!pix || expired) && (
          <button type="button" className={classes.secondaryBtn} onClick={retryCard} disabled={!!busy}>
            {busy === "retry" ? t("overdue.retrying") : card}
          </button>
        )}
        <button type="button" className={classes.secondaryBtn} onClick={onReload} disabled={!!busy}>
          <RefreshCw />
          {t("overdue.refresh")}
        </button>
      </div>
    </Paper>
  );
};

const PlansOverview = ({ overview, onReload, onProfileSaved }) => {
  const classes = useStyles();
  const { refetchBilling } = useBilling();
  const [dialog, setDialog] = useState(null);
  const [adequacy, setAdequacy] = useState(null);
  const [refunding, setRefunding] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [confirmUndo, setConfirmUndo] = useState(false);
  const [busy, setBusy] = useState(false);
  const confirmButton = useRef(null);
  const undoButton = useRef(null);
  // Os botões que voltam a receber o foco quando a confirmação some (manter, desfazer).
  const cancelTrigger = useRef(null);
  const undoTrigger = useRef(null);
  const reduceButton = useRef(null);
  const focusAfter = useRef(null);
  // O plano escolhido no site (?plan=, do fim do cadastro): a contratação abre com ele.
  const [preferredPlan, setPreferredPlan] = useState(null);
  const location = useLocation();
  const history = useHistory();
  const { catalog, plan, terms, limits, usage, messages, subscription, pendingRequest, scheduledReduction, invoices, courtesy, enabled, adequacyRequired, archivedCount, delinquency, disputes, refunds, conditions, adequacyDueAt } = overview;
  // As condições combinadas (etapa 11), numa frase cada: o preço, o desconto, a cortesia de um item e o prazo.
  const conditionLines = (conditions || [])
    .filter((condition) => !(condition.kind === "courtesy" && condition.item === "all"))
    .map((condition) => {
      // Cada frase com o item na forma certa da língua ("da licença", "na mensalidade").
      const text =
        condition.kind === "price"
          ? t("conditions.price", { item: t(`conditions.itemsOf.${condition.item}`, { defaultValue: condition.item }), amount: money(condition.value || 0) })
          : condition.kind === "discount"
            ? t("conditions.discount", { value: condition.value, item: t(`conditions.itemsOn.${condition.item}`, { defaultValue: condition.item }) })
            : condition.item === "user_license"
              ? t("conditions.courtesyLicense")
              : t("conditions.courtesy", { item: t(`conditions.items.${condition.item}`, { defaultValue: condition.item }) });
      const starts = condition.startsAt && new Date(condition.startsAt) > new Date();
      const period = starts
        ? t("conditions.from", { date: formatSaoPauloDate(condition.startsAt) })
        : condition.endsAt
          ? t("conditions.until", { date: formatSaoPauloDate(condition.endsAt) })
          : "";
      return `${text}${period}.`;
    });
  const definition = catalog.plans.find((item) => item.key === plan);
  const starter = plan === "starter";
  const canceling = scheduledReduction?.kind === "cancellation";
  // O cancelamento desfeito: a assinatura nova só começa no fim do ciclo pago.
  const resumed = subscription?.status === "future";

  const summary = () => {
    if (starter) {
      const base = t("starterSummary", {
        users: definition.maxUsers,
        accounts: definition.accounts,
        whatsapps: definition.whatsapps,
        boards: definition.crmBoards,
        common: thousands(definition.commonMessages),
        ai: thousands(definition.aiMessages),
      });
      return terms.internalChatSeats > 0 ? `${base} ${t("summary.starterInternal", { count: terms.internalChatSeats })}.` : base;
    }
    const parts = [t("summary.seats", { count: limits.users })];
    if (terms.internalChatSeats > 0) parts.push(t("summary.internal", { count: terms.internalChatSeats }));
    if (terms.aiPackMessages) parts.push(t("summary.pack", { messages: thousands(terms.aiPackMessages) }));
    return `${capitalize(sentence(parts))}.`;
  };

  const pill = () => {
    if (courtesy) return <span className={classes.pill}>{t("status.courtesy")}</span>;
    if (subscription?.status === "failed") return <span className={`${classes.pill} ${classes.pillWait}`}>{t("status.failed")}</span>;
    if (canceling) return <span className={`${classes.pill} ${classes.pillWait}`}>{t("status.canceling")}</span>;
    if (resumed) return <span className={classes.pill}>{t("status.resumed")}</span>;
    if (subscription) return <span className={`${classes.pill} ${classes.pillOk}`}>{t("status.active")}</span>;
    return null;
  };

  const renews = () => {
    if (canceling) return t("paidUntil", { date: formatUtcDate(scheduledReduction.effectiveAt) });
    if (resumed && subscription.startsAt) return t("resumeStarts", { date: formatUtcDate(subscription.startsAt) });
    if (!subscription?.nextBillingAt) return null;
    const date = formatUtcDate(subscription.nextBillingAt);
    if (subscription.paymentMethod === "boleto") return t("renewsBoleto", { date });
    // Sem a bandeira ou o final (a Pagar.me ainda não mandou), o cartão cadastrado.
    return subscription.cardBrand && subscription.cardLastFour
      ? t("renewsCard", { date, brand: capitalize(subscription.cardBrand), last4: subscription.cardLastFour })
      : t("renewsSavedCard", { date });
  };

  // Desistir (e desfazer a redução) em dois passos: o foco vai para a confirmação e, ao manter, volta ao botão.
  useEffect(() => {
    if (confirmCancel && confirmButton.current) confirmButton.current.focus();
    else if (!confirmCancel && focusAfter.current === "cancel" && cancelTrigger.current) cancelTrigger.current.focus();
  }, [confirmCancel]);
  useEffect(() => {
    if (confirmUndo && undoButton.current) undoButton.current.focus();
    else if (!confirmUndo && focusAfter.current === "undo" && undoTrigger.current) undoTrigger.current.focus();
  }, [confirmUndo]);
  // Desfeita a redução, o cartão some: o foco vai para "Reduzir na renovação" (de novo ligado).
  useEffect(() => {
    if (!scheduledReduction && focusAfter.current === "reduce" && reduceButton.current) {
      focusAfter.current = null;
      reduceButton.current.focus();
    }
  }, [scheduledReduction]);

  const finishDialog = () => {
    setDialog(null);
    onReload();
    refetchBilling();
  };

  // A escolha do que fica: lida do servidor na hora de abrir (os itens podem ter mudado).
  const openAdequacy = async () => {
    try {
      const { data } = await api.get("/billing/adequacy");
      if (!data.adequacy) {
        toast.info(t("adequacy.notNeeded"));
        onReload();
        return;
      }
      setAdequacy(data.adequacy);
      setDialog("adequacy");
    } catch (err) {
      toastError(err);
    }
  };

  const adequacySaved = () => {
    const mode = adequacy?.mode;
    setDialog(null);
    setAdequacy(null);
    toast.success(mode === "restricted" ? t("adequacy.doneRestricted") : mode === "due" ? t("adequacy.doneDue") : t("adequacy.saved"));
    onReload();
    refetchBilling();
  };


  const cancelRequest = async () => {
    setBusy(true);
    try {
      await api.post(`/billing/checkout/${pendingRequest.id}/cancel`);
      toast.success(t("pending.canceled"));
      setConfirmCancel(false);
      onReload();
    } catch (err) {
      toastError(err);
      onReload();
    } finally {
      setBusy(false);
    }
  };

  const undoReduction = async () => {
    setBusy(true);
    try {
      await api.post(`/billing/reductions/${scheduledReduction.id}/cancel`);
      toast.success(canceling ? t("cancellation.undone") : t("reduction.undone"));
      focusAfter.current = "reduce";
      setConfirmUndo(false);
      onReload();
    } catch (err) {
      // Na última hora antes da renovação não dá mais para desfazer (e depois dela a redução já vale).
      const code = err?.response?.data?.error;
      if (code === "ERR_BILLING_RENEWING") toast.info(canceling ? t("cancellation.locked") : t("reduction.locked"));
      else if (code) toast.error(billingErrorText(code));
      else toastError(err);
      setConfirmUndo(false);
      onReload();
    } finally {
      setBusy(false);
    }
  };

  const refreshRequest = async () => {
    setBusy(true);
    try {
      await api.get(`/billing/checkout/${pendingRequest.id}`);
      onReload();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  // A contratação parte do Starter sem assinatura.
  const canContract = starter && !courtesy && !pendingRequest && !subscription;
  useEffect(() => {
    const wanted = new URLSearchParams(location.search).get("plan");
    if (wanted !== "basic" && wanted !== "pro") return;
    history.replace(location.pathname);
    if (!enabled || !canContract) return;
    setPreferredPlan(wanted);
    setDialog("upgrade");
  }, [location.search, location.pathname, history, enabled, canContract]);
  // A alteração (etapa 8): no Starter, só mais Chat Interno, até o máximo de pessoas dele (ir ao Basic ou ao Pro: o suporte).
  const internalRoom = (terms.internalChatSeats || 0) < (definition.maxUsers || 0);
  // A assinatura recusada, ou uma fatura vencida (etapa 10): primeiro pagar, depois mudar o plano.
  const paymentPending = subscription?.status === "failed" || !!delinquency?.charges?.length;
  const canChange = enabled && !pendingRequest && !paymentPending && !scheduledReduction && !resumed && (!starter || internalRoom);
  // A redução (etapa 9): menos do que tem, valendo na renovação, com os mesmos mínimos do modal (o que a Empresa usa, o
  // mínimo cobrado). No Starter, só o Chat Interno, e nunca abaixo de 1 (zerar é o cancelamento).
  const reducible = starter
    ? (terms.internalChatSeats || 0) > Math.max(1, usage.internalChatUsers)
    : plan === "pro" ||
      (terms.seatsContracted || 0) > Math.max(definition.minBilledUsers, usage.users) ||
      (terms.internalChatSeats || 0) > usage.internalChatUsers ||
      !!terms.aiPackMessages;
  const canReduce = enabled && !pendingRequest && !paymentPending && !scheduledReduction && !resumed && reducible;
  // Cancelar (etapa 9): ir para o Starter sem nada pago; no Starter, é cancelar o Chat Interno. Com a assinatura retomada
  // ainda por começar, dá para cancelar de novo (nada foi cobrado).
  const canCancel = enabled && !pendingRequest && !paymentPending && !scheduledReduction;
  // Desfazer só até a última hora antes da renovação (o servidor diz até quando).
  const undoLocked = !!scheduledReduction?.undoUntil && new Date(scheduledReduction.undoUntil).getTime() <= Date.now();
  // O dia em que a assinatura do "desfazer" começa: o seguinte ao fim do ciclo pago.
  const resumeDate = scheduledReduction ? formatUtcDate(new Date(new Date(scheduledReduction.effectiveAt).getTime() + 1000)) : null;
  const paidWith =
    subscription?.paymentMethod === "boleto"
      ? t("cancellation.byBoleto")
      : subscription?.cardBrand && subscription?.cardLastFour
        ? t("cancellation.byCard", { brand: capitalize(subscription.cardBrand), last4: subscription.cardLastFour })
        : t("cancellation.bySavedCard");
  const changeRequest = pendingRequest?.kind === "change";
  const pendingTitle =
    pendingRequest?.paymentMethod === "pix" ? t("pending.titlePix") : pendingRequest?.paymentMethod === "boleto" ? t("pending.titleBoleto") : t("pending.titleCard");

  return (
    <>
      {delinquency && <OverdueCard classes={classes} delinquency={delinquency} subscription={subscription} onReload={onReload} />}

      {/* A contestação no cartão em análise (etapa 10): enquanto a operadora decide, nada muda. */}
      {disputes?.length > 0 && (
        <Paper elevation={0} className={classes.card} component="section" aria-labelledby="billing-disputes-title">
          <div className={classes.cardHead}>
            <h2 className={classes.cardTitle} id="billing-disputes-title">
              {t("disputes.title")}
            </h2>
          </div>
          {disputes.map((dispute) => (
            <p key={dispute.chargeId} className={classes.pendingText}>
              {t("disputes.text", { amount: money(dispute.amountCents), date: dispute.disputedAt ? formatDate(dispute.disputedAt) : "" })}
            </p>
          ))}
        </Paper>
      )}

      {adequacyRequired && (
        <Paper elevation={0} className={`${classes.card} ${classes.restrictedCard}`} component="section" aria-labelledby="billing-restricted-title" role="region">
          <div className={classes.cardHead}>
            <h2 className={classes.cardTitle} id="billing-restricted-title">
              {t("restricted.title")}
            </h2>
          </div>
          <p className={classes.pendingText}>{t("restricted.text", { plan: definition.name })}</p>
          <div className={classes.pendingActions}>
            <button type="button" className={classes.primaryBtn} onClick={openAdequacy}>
              {t("restricted.action")}
            </button>
          </div>
        </Paper>
      )}

      {!adequacyRequired && adequacyDueAt && (
        <Paper elevation={0} className={`${classes.card} ${classes.restrictedCard}`} component="section" aria-labelledby="billing-due-title" role="region">
          <div className={classes.cardHead}>
            <h2 className={classes.cardTitle} id="billing-due-title">
              {t("adequacyDue.title", { date: formatSaoPauloDate(adequacyDueAt) })}
            </h2>
          </div>
          <p className={classes.pendingText}>{t("adequacyDue.text", { plan: definition.name, date: formatSaoPauloDate(adequacyDueAt) })}</p>
          <div className={classes.pendingActions}>
            <button type="button" className={classes.primaryBtn} onClick={openAdequacy}>
              {t("adequacyDue.action")}
            </button>
          </div>
        </Paper>
      )}

      <Paper elevation={0} className={classes.card} component="section" aria-labelledby="billing-plan-name">
        <div className={classes.plan}>
          <div className={classes.planMain}>
            <div className={classes.planNameRow}>
              <h2 className={classes.planName} id="billing-plan-name">
                {definition.name}
              </h2>
              {pill()}
            </div>
            <p className={classes.planSentence}>{summary()}</p>
            {renews() && <p className={classes.planRenew}>{renews()}</p>}
          </div>
          {!courtesy && (
            <div className={classes.price}>
              <span className={classes.priceValue}>{subscription ? money(subscription.monthlyCents) : t("free")}</span>
              {subscription && <span className={classes.priceUnit}>{t("perMonth")}</span>}
              {/* As condições combinadas com a Sttelar (etapa 11): o preço do catálogo fica ao lado. */}
              {subscription && subscription.listMonthlyCents !== subscription.monthlyCents && (
                <span className={classes.priceList}>{t("conditions.list", { amount: money(subscription.listMonthlyCents) })}</span>
              )}
            </div>
          )}
        </div>
        {courtesy && <p className={classes.planNote}>{t("courtesyNote")}</p>}
        {!courtesy && conditionLines.length > 0 && (
          <ul className={classes.conditionList} aria-label={t("conditions.title")}>
            {conditionLines.map((line, index) => (
              <li key={`${index}-${line}`}>{line}</li>
            ))}
          </ul>
        )}
        {starter && !courtesy && !subscription && (
          <>
            <div className={classes.planActions}>
              <button type="button" className={classes.primaryBtn} onClick={() => setDialog("upgrade")} disabled={!enabled || !canContract}>
                {t("actions.upgrade")}
              </button>
              <button type="button" className={classes.secondaryBtn} onClick={() => setDialog("internal")} disabled={!enabled || !canContract}>
                {t("actions.addInternal")}
              </button>
            </div>
            {!enabled && <p className={classes.planNote}>{t("disabledNote")}</p>}
          </>
        )}
        {subscription && !courtesy && (
          <>
            <div className={classes.planActions}>
              <button type="button" className={classes.secondaryBtn} onClick={() => setDialog("change")} disabled={!canChange}>
                {starter ? t("actions.changeInternal") : t("actions.change")}
              </button>
              <button type="button" ref={reduceButton} className={classes.secondaryBtn} onClick={() => setDialog("reduce")} disabled={!canReduce}>
                {t("actions.reduce")}
              </button>
              <button type="button" className={classes.secondaryBtn} onClick={() => setDialog("cancel")} disabled={!canCancel}>
                {starter ? t("actions.cancelInternal") : t("actions.cancel")}
              </button>
            </div>
            {!enabled ? (
              <p className={classes.planNote}>{t("disabledNote")}</p>
            ) : paymentPending ? (
              <p className={classes.planNote}>{t("paymentPendingNote")}</p>
            ) : resumed ? (
              <p className={classes.planNote}>{t("resumeNote")}</p>
            ) : canceling ? (
              <p className={classes.planNote}>{t("cancellationScheduledNote")}</p>
            ) : scheduledReduction ? (
              <p className={classes.planNote}>{t("reductionScheduledNote")}</p>
            ) : starter ? (
              <p className={classes.planNote}>{t("starterChangeNote")}</p>
            ) : !reducible ? (
              <p className={classes.planNote}>{t("nothingToReduceNote")}</p>
            ) : null}
          </>
        )}
        {!subscription && !starter && !courtesy && <p className={classes.planNote}>{t("changeNote")}</p>}
      </Paper>

      {scheduledReduction && (
        <Paper elevation={0} className={classes.card} component="section" aria-labelledby="billing-reduction-title">
          <div className={classes.cardHead}>
            <h3 className={classes.cardTitle} id="billing-reduction-title">
              {canceling
                ? t(starter ? "cancellation.titleInternal" : "cancellation.title", { date: formatUtcDate(scheduledReduction.effectiveAt) })
                : t("reduction.title", { date: formatUtcDate(scheduledReduction.effectiveAt) })}
            </h3>
          </div>
          <p className={classes.pendingText}>
            {canceling
              ? starter
                ? t("cancellation.textInternal")
                : t("cancellation.text", { plan: definition.name })
              : t("reduction.text", { what: describeTerms(catalog, scheduledReduction.changes), amount: money(scheduledReduction.amountCents) })}
          </p>
          {(scheduledReduction.needsChoice || scheduledReduction.hasChoice) && (
            <div className={classes.choiceRow}>
              <p className={scheduledReduction.needsChoice ? classes.choiceWarn : classes.planNote}>
                {scheduledReduction.needsChoice ? t("adequacy.needed", { date: formatUtcDate(scheduledReduction.effectiveAt) }) : t("adequacy.chosen")}
              </p>
              <button type="button" className={scheduledReduction.needsChoice ? classes.primaryBtn : classes.secondaryBtn} onClick={openAdequacy}>
                {scheduledReduction.needsChoice ? t("adequacy.choose") : t("adequacy.review")}
              </button>
            </div>
          )}
          {undoLocked ? (
            <p className={classes.planNote}>{canceling ? t("cancellation.locked") : t("reduction.locked")}</p>
          ) : (
            <div className={classes.pendingActions}>
              {confirmUndo ? (
                <>
                  {canceling && <p className={classes.undoHint}>{t("cancellation.undoHint", { plan: definition.name, date: resumeDate, paidWith })}</p>}
                  <button type="button" ref={undoButton} className={`${classes.secondaryBtn} ${canceling ? "" : classes.dangerBtn}`} onClick={undoReduction} disabled={busy}>
                    {canceling ? t("cancellation.undoConfirm") : t("reduction.undoConfirm")}
                  </button>
                  <button
                    type="button"
                    className={classes.secondaryBtn}
                    onClick={() => {
                      focusAfter.current = "undo";
                      setConfirmUndo(false);
                    }}
                    disabled={busy}
                  >
                    {canceling ? t("cancellation.keep") : t("reduction.keep")}
                  </button>
                </>
              ) : (
                <button type="button" ref={undoTrigger} className={classes.secondaryBtn} onClick={() => setConfirmUndo(true)} disabled={busy}>
                  {canceling ? t("cancellation.undo") : t("reduction.undo")}
                </button>
              )}
            </div>
          )}
        </Paper>
      )}

      {pendingRequest && (
        <Paper elevation={0} className={classes.card} component="section" aria-labelledby="billing-pending-title">
          <div className={classes.cardHead}>
            <h3 className={classes.cardTitle} id="billing-pending-title">
              {pendingTitle}
            </h3>
            {pendingRequest.boleto?.dueAt && <span className={classes.cardMeta}>{t("pending.due", { date: formatUtcDate(pendingRequest.boleto.dueAt) })}</span>}
          </div>
          <p className={classes.pendingText}>
            {pendingRequest.status === "creating"
              ? changeRequest
                ? t("pending.verifyingChange")
                : t("pending.verifying")
              : changeRequest
                ? t("pending.textChange", { amount: money(pendingRequest.prorationCents ?? 0), what: describeTerms(catalog, pendingRequest.changes) })
                : t("pending.text", { amount: money(pendingRequest.amountCents), what: describeTerms(catalog, pendingRequest.changes) })}
          </p>
          {pendingRequest.paymentMethod === "pix" && pendingRequest.status === "awaiting_payment" && (
            <div className={classes.pixBlock}>
              <PixPayment pix={pendingRequest.pix} amountCents={pendingRequest.prorationCents ?? 0} expiredHint={t("pending.pixExpiredHint")} />
            </div>
          )}
          {pendingRequest.boleto?.line && (
            <div className={classes.boletoLine}>
              <span className={classes.boletoDigits}>
                <span className={classes.boletoLabel}>{t("pending.line")}</span>
                {formatBoletoLine(pendingRequest.boleto.line)}
              </span>
              <button type="button" className={classes.secondaryBtn} onClick={() => copyBoletoLine(pendingRequest.boleto.line)}>
                <Copy />
                {t("pending.copy")}
              </button>
            </div>
          )}
          <div className={classes.pendingActions}>
            {pendingRequest.boleto?.url && (
              <a className={classes.secondaryBtn} href={pendingRequest.boleto.url} target="_blank" rel="noopener noreferrer">
                <ExternalLink />
                {t("pending.open")}
              </a>
            )}
            <button type="button" className={classes.secondaryBtn} onClick={refreshRequest} disabled={busy}>
              <RefreshCw />
              {t("pending.refresh")}
            </button>
            {/* A alteração em verificação não deixa desistir: o pedido pode aparecer pago. */}
            {changeRequest && pendingRequest.status === "creating" ? null : confirmCancel ? (
              <>
                <button type="button" ref={confirmButton} className={`${classes.secondaryBtn} ${classes.dangerBtn}`} onClick={cancelRequest} disabled={busy}>
                  {t("pending.cancelConfirm")}
                </button>
                <button
                  type="button"
                  className={classes.secondaryBtn}
                  onClick={() => {
                    focusAfter.current = "cancel";
                    setConfirmCancel(false);
                  }}
                  disabled={busy}
                >
                  {pendingRequest.paymentMethod === "boleto" ? t("pending.keepBoleto") : t("pending.keep")}
                </button>
                {pendingRequest.paymentMethod === "boleto" && <span className={classes.cancelHint}>{t("pending.cancelHint")}</span>}
              </>
            ) : (
              <button type="button" ref={cancelTrigger} className={classes.secondaryBtn} onClick={() => setConfirmCancel(true)} disabled={busy}>
                {t("pending.cancel")}
              </button>
            )}
          </div>
        </Paper>
      )}

      <Paper elevation={0} className={classes.card} component="section" aria-labelledby="billing-usage-title">
        <div className={classes.cardHead}>
          <h3 className={classes.cardTitle} id="billing-usage-title">
            {t("usage.title")}
            <InfoTip text={t("usage.info")} />
          </h3>
          <span className={classes.cardMeta}>{t("usage.period", { start: formatDate(messages.startsAt), end: formatDate(new Date(new Date(messages.endsAt).getTime() - 1)) })}</span>
        </div>
        <ul className={classes.usageList}>
          <UsageRow classes={classes} name={t("usage.users")} used={usage.users} limit={limits.users} />
          <UsageRow classes={classes} name={t("usage.internalChat")} used={usage.internalChatUsers} limit={limits.internalChatUsers} />
          <UsageRow classes={classes} name={t("usage.common")} used={messages.common.used} limit={messages.common.limit} consumable />
          <UsageRow classes={classes} name={t("usage.ai")} used={messages.ai.used} limit={messages.ai.limit} consumable />
          <UsageRow classes={classes} name={t("usage.accounts")} used={usage.accounts} limit={limits.accounts} />
          <UsageRow classes={classes} name={t("usage.whatsapps")} used={usage.whatsapps} limit={limits.whatsapps} />
          <UsageRow classes={classes} name={t("usage.crmBoards")} used={usage.crmBoards} limit={limits.crmBoards} />
        </ul>
      </Paper>

      <Paper elevation={0} className={classes.card} component="section" aria-labelledby="billing-invoices-title">
        <div className={classes.cardHead}>
          <h3 className={classes.cardTitle} id="billing-invoices-title">
            {t("invoices.title")}
          </h3>
        </div>
        {invoices.length === 0 ? (
          <p className={classes.empty}>{t("invoices.empty")}</p>
        ) : (
          <div className={classes.tableWrap}>
            <table className={classes.table}>
              <thead>
                <tr>
                  <th className={classes.th}>{t("invoices.date")}</th>
                  <th className={classes.th}>{t("invoices.amount")}</th>
                  <th className={classes.th}>{t("invoices.method")}</th>
                  <th className={classes.th}>{t("invoices.status")}</th>
                  <th className={classes.th} aria-label={t("invoices.openBoleto")} />
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => {
                  // O boleto de um pedido desistido ou expirado continua "pendente" na Pagar.me até vencer: não pague.
                  const abandoned = invoice.abandoned && invoice.status === "pending";
                  // A fatura vencida trocada pelo Pix (etapa 10) aparece cancelada na Pagar.me, mas foi (ou vai ser) paga no Pix.
                  // O estorno feito pela Sttelar (etapa 10): por inteiro, a Pagar.me diz "cancelada"; em parte, segue paga.
                  const paid = invoice.paidAmountCents ?? invoice.amountCents;
                  const refunded = invoice.refundedCents > 0 && invoice.refundedCents >= paid;
                  const partlyRefunded = invoice.refundedCents > 0 && !refunded;
                  const tone = abandoned ? "bad" : invoice.replaced || refunded ? "" : STATUS_TONE[invoice.status];
                  const toneClass = tone === "ok" ? classes.statusOk : tone === "wait" ? classes.statusWait : tone === "bad" ? classes.statusBad : "";
                  return (
                    <tr key={invoice.id} className={classes.tr}>
                      <td className={classes.td} data-label={t("invoices.date")}>
                        {formatDate(invoice.createdAt)}
                      </td>
                      <td className={classes.td} data-label={t("invoices.amount")}>
                        {money(paid)}
                      </td>
                      <td className={`${classes.td} ${classes.tdMuted}`} data-label={t("invoices.method")}>
                        {invoice.paymentMethod ? t(`invoices.${invoice.paymentMethod}`, { defaultValue: invoice.paymentMethod }) : ""}
                      </td>
                      <td className={`${classes.td} ${toneClass}`} data-label={t("invoices.status")}>
                        {abandoned
                          ? t("invoices.abandoned")
                          : invoice.replaced
                            ? t("invoices.replaced")
                            : refunded
                              ? t("invoices.statuses.refunded")
                              : partlyRefunded
                                ? t("invoices.partlyRefunded", { amount: money(invoice.refundedCents) })
                                : t(`invoices.statuses.${invoice.status}`, { defaultValue: invoice.status })}
                      </td>
                      <td className={classes.td}>
                        {invoice.boletoUrl && invoice.status === "pending" && !abandoned && (
                          <a className={classes.link} href={invoice.boletoUrl} target="_blank" rel="noopener noreferrer">
                            {t("invoices.openBoleto")}
                          </a>
                        )}
                        {/* O estorno (etapa 10): a Sttelar decide; aqui só se pede. */}
                        {invoice.refundable && (
                          <button type="button" className={classes.linkBtn} onClick={() => setRefunding(invoice)}>
                            {t("invoices.refund")}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Paper>

      {refunds?.length > 0 && (
        <Paper elevation={0} className={classes.card} component="section" aria-labelledby="billing-refunds-title">
          <div className={classes.cardHead}>
            <h3 className={classes.cardTitle} id="billing-refunds-title">
              {t("refunds.title")}
            </h3>
          </div>
          <ul className={classes.overdueList} style={{ maxWidth: "none" }}>
            {refunds.map((refund) => (
              <li key={refund.id} className={classes.overdueItem}>
                <span style={{ minWidth: 0 }}>
                  {t("refunds.item", { amount: money(refund.amountCents), date: formatDate(refund.createdAt) })}
                  {refund.decisionReason && (refund.status === "refused" || refund.status === "canceled") && (
                    <span className={classes.refundNote}>{t(refund.status === "refused" ? "refunds.refusedReason" : "refunds.canceledReason", { reason: refund.decisionReason })}</span>
                  )}
                </span>
                <span className={refund.status === "refunded" ? classes.statusOk : refund.status === "refused" || refund.status === "canceled" ? classes.statusBad : classes.statusWait}>
                  {t(`refunds.statuses.${refund.status}`, { defaultValue: refund.status })}
                </span>
              </li>
            ))}
          </ul>
        </Paper>
      )}

      {archivedCount > 0 && <ArchivedCard classes={classes} count={archivedCount} onRestored={onReload} />}

      {refunding && (
        <RefundDialog
          invoice={refunding}
          onClose={() => setRefunding(null)}
          onSent={() => {
            setRefunding(null);
            onReload();
          }}
        />
      )}

      {(dialog === "reduce" || dialog === "cancel") && (
        <ReductionDialog
          open
          mode={dialog}
          overview={overview}
          onClose={() => setDialog(null)}
          onDone={finishDialog}
          onChoose={() => {
            finishDialog();
            openAdequacy();
          }}
        />
      )}
      {dialog === "adequacy" && adequacy && (
        <AdequacyDialog
          open
          adequacy={adequacy}
          planName={catalog.plans.find((item) => item.key === adequacy.target.plan)?.name}
          onClose={() => setDialog(null)}
          onSaved={adequacySaved}
        />
      )}
      {dialog && !["reduce", "cancel", "adequacy"].includes(dialog) && (
        <PlansDialog
          open
          mode={dialog}
          overview={overview}
          preferredPlan={dialog === "upgrade" ? preferredPlan : null}
          onClose={() => setDialog(null)}
          onDone={finishDialog}
          onProfileSaved={onProfileSaved}
        />
      )}
    </>
  );
};

/** A Empresa de antes dos planos: nada a contratar por aqui; a passagem para um plano é combinada com a Sttelar. */
const LegacyNotice = ({ name }) => {
  const classes = useStyles();
  return (
    <Paper elevation={0} className={classes.card} component="section" aria-labelledby="billing-legacy-title">
      <div className={classes.planMain}>
        <h2 className={classes.planName} id="billing-legacy-title">
          {t("legacy.title")}
        </h2>
        <p className={classes.planSentence}>{t("legacy.text")}</p>
      </div>
      <div className={classes.planActions}>
        <a className={classes.secondaryBtn} href={sttelarWhatsAppUrl(t("legacy.message", { name }))} target="_blank" rel="noopener noreferrer">
          {t("legacy.talk")}
          <ExternalLink aria-hidden="true" />
        </a>
      </div>
    </Paper>
  );
};

/** A seção Faturamento: os planos novos (com os dados para a cobrança) ou o aviso das Empresas de antes deles. */
const BillingOverview = ({ onDirtyChange }) => {
  const [overview, setOverview] = useState(null);
  const [error, setError] = useState(false);
  const socketManager = useContext(SocketContext);
  // Salvos na contratação, os dados voltam do servidor também para o formulário da página.
  const [profileVersion, setProfileVersion] = useState(0);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/billing/overview");
      setOverview(data);
      setError(false);
    } catch (err) {
      setError(true);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    if (!companyId || !socketManager) return undefined;
    const socket = socketManager.getSocket(companyId);
    const onBilling = () => load();
    socket.on(`company-${companyId}-billing`, onBilling);
    return () => socket.off(`company-${companyId}-billing`, onBilling);
  }, [socketManager, load]);

  const profileSaved = useCallback(() => {
    setProfileVersion((version) => version + 1);
    load();
  }, [load]);

  // Só sem nada para mostrar: uma releitura que falha não fecha o modal aberto nem o que foi digitado.
  if (error && !overview) return <LoadError onRetry={load} message={t("loadError")} />;
  if (!overview) return <LoadingState />;
  if (overview.legacy) return <LegacyNotice name={overview.name} />;
  return (
    <>
      <PlansOverview overview={overview} onReload={load} onProfileSaved={profileSaved} />
      <BillingProfileForm key={profileVersion} onDirtyChange={onDirtyChange} onSaved={load} />
    </>
  );
};

export default BillingOverview;
